import { captureException } from '@sentry/browser'
import clsx from 'clsx'
import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { IconArrowDown } from 'components/Icons'
import ProgressTable from './TicketProgressTable'
import { findProgressLogs } from 'features/Ticket'

import type { IResponseProgressLogs } from 'features/Ticket'
import type { IColumn } from 'types'
import type {
  IUpdateDetailProduct,
  ITaskTransactionProduct,
} from 'features/Ticket'
import type { Product } from 'features/Ticket/TicketProductInfo'

type PropsProgressInfo = {
  openProgress: boolean
  columns: IColumn[]
  ticketId: string
  products: Product[]
  onOpenProgress: () => void
  tasksButtonElement: React.ReactNode
}

const TicketProgressInfo: React.FC<PropsProgressInfo> = (props) => {
  const { t } = useTranslation('ticket')

  const [progressLogs, setProgressLogs] = useState<IResponseProgressLogs[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [isError, setIsError] = useState<boolean>(false)

  const {
    ticketId,
    columns,
    openProgress,
    products,
    onOpenProgress,
    tasksButtonElement,
  } = props

  const base = 'f-ticket-progress-info'

  useEffect(() => {
    if (ticketId && openProgress) {
      fetchProgressLogs(ticketId)
    }
  }, [ticketId, openProgress])

  const fetchProgressLogs = async (ticketId: string) => {
    try {
      const { data = [] } = await findProgressLogs(ticketId)

      setProgressLogs(data)
      setIsLoading(false)
      setIsError(false)
    } catch (error) {
      captureException(error)

      setIsError(true)
    }
  }

  const handleClickShowProgress = () => {
    setIsLoading(true)
    onOpenProgress()
  }

  const getProductName = (
    products: Product[],
    taskTransactionProductId?: string,
  ) => {
    return products.find((item) => item.id === taskTransactionProductId)?.name
  }

  const handleGetProductValue = (
    updateDetail: IUpdateDetailProduct | null,
    list: ITaskTransactionProduct[],
  ): ITaskTransactionProduct | null => {
    if (updateDetail && list) {
      const data = list.find((item) => {
        return (
          updateDetail.type === 'product' &&
          item.productId === updateDetail.refId
        )
      })

      return {
        productId: data?.productId || '',
        productName: getProductName(products, data?.productId),
        beforePhotos: data?.beforePhotos || null,
        afterPhotos: data?.afterPhotos || null,
      }
    }

    return null
  }

  return (
    <div className={base}>
      <div className={`${base}__view`}>
        <div
          className={`${base}__row`}
          data-testid='progress-log-wrapper'
          onClick={handleClickShowProgress}
        >
          <span className={`${base}__title`} data-testid='progress-log-title'>
            {t('progressLog')}
          </span>
          <IconArrowDown
            className={clsx(openProgress && `${base}__open-view`)}
            variants='sm'
          />
        </div>
        {tasksButtonElement}
      </div>

      {openProgress && (
        <ProgressTable
          columns={columns}
          progressLogs={progressLogs}
          isLoading={isLoading}
          isError={isError}
          getProductValue={handleGetProductValue}
        />
      )}
    </div>
  )
}

export default TicketProgressInfo
